import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { ContentViewComponent } from '@principle-theorem/ng-interactions';
import {
  CurrentScopeFacade,
  ManagementService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  CustomTooltipModule,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import {
  SetupEngagementTracker,
  type IPracticeSetupStepDefinition,
} from '@principle-theorem/principle-core';
import {
  IPracticeSetupStep,
  PracticeSetupStepStatus,
  PracticeSetupWizardStatus,
} from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, snapshotDefined } from '@principle-theorem/shared';
import { filter, map, startWith } from 'rxjs/operators';
import { PracticeSetupStepStatusComponent } from '../practice-setup-step-status/practice-setup-step-status.component';
import { SetupStepStore } from '../setup-step.store';
import { PracticeSetupWizardStore } from '../setup-wizard.store';

@Component({
  selector: 'pr-setup-step-banner',
  templateUrl: './setup-step-banner.component.html',
  styleUrl: './setup-step-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SetupStepStore],
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    CustomTooltipModule,
    PracticeSetupStepStatusComponent,
    ContentViewComponent,
  ],
})
export class SetupStepBannerComponent {
  private _router = inject(Router);
  private _organisationService = inject(OrganisationService);
  private _management = inject(ManagementService);
  private _currentScope = inject(CurrentScopeFacade);
  private _snackBar = inject(MatSnackBar);
  private _routeToStepMap: Signal<Map<string, IPracticeSetupStep[]>>;
  private readonly _currentStaffer = toSignal(
    this._organisationService.staffer$
  );
  private _practice$ =
    this._currentScope.currentPractice$.pipe(filterUndefined());

  private readonly _currentUrl = toSignal(
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this._router.url),
      startWith(this._router.url)
    ),
    { initialValue: this._router.url }
  );

  store = inject(PracticeSetupWizardStore);
  stepStore = inject(SetupStepStore);
  brand = toSignal(this._organisationService.brand$);
  practice = toSignal(this._organisationService.practice$);
  isManagement: Signal<boolean>;
  markCompleteTooltip: Signal<{
    title: string;
    errors: string[];
  }>;
  matchingSteps = computed(() => this._computeMatchingSteps());
  selectedStepIndex = signal(0);

  matchingStep = computed(() => {
    const steps = this.matchingSteps();
    const index = this.selectedStepIndex();
    return steps[index] ?? steps[0];
  });

  showBanner = computed(() => {
    const wizard = this.store.wizard();
    if (!wizard || wizard.status === PracticeSetupWizardStatus.Complete) {
      return false;
    }

    return !!this.matchingStep();
  });

  tooltipPositions: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
    },
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
    },
  ];

  constructor() {
    const managementUser = toSignal(this._management.user$, {
      initialValue: undefined,
    });
    this.isManagement = computed(() => !!managementUser());

    effect(() => {
      const practice = this.practice();
      const wizard = this.store.wizard();
      const loading = this.store.loading();

      if (practice && !wizard && !loading) {
        this.store.loadWizard(this._practice$);
      }
    });

    effect(() => {
      const steps = this.matchingSteps();
      const firstIncompleteIndex = steps.findIndex(
        (step) => step.status !== PracticeSetupStepStatus.Complete
      );
      this.selectedStepIndex.set(Math.max(firstIncompleteIndex, 0));
    });

    effect(() => {
      this.stepStore.setStep(this.matchingStep());
    });

    effect(() => {
      const steps = this.matchingSteps();
      const practice = this.practice();
      const wizard = this.store.wizard();
      const staffer = this._currentStaffer();

      if (!steps.length || !practice) {
        return;
      }

      if (wizard?.ref && staffer?.ref) {
        void SetupEngagementTracker.recordEngagement(wizard.ref, staffer.ref);
      }

      const currentUrl = this._currentUrl();
      if (!currentUrl) {
        return;
      }
      const route = this._normalizeUrl(currentUrl);

      for (const step of steps) {
        if (step.status === PracticeSetupStepStatus.Complete) {
          continue;
        }

        const definition = this._findStepDefinition(step.id);
        if (!definition?.pageVisitTracking) {
          continue;
        }

        this.store.trackVisit({ stepId: step.id, route });
      }
    });

    this._routeToStepMap = computed(() => {
      const brand = this.brand();
      const practice = this.practice();
      const steps = this.store.steps();

      if (!brand || !practice || !steps?.length) {
        return new Map<string, IPracticeSetupStep[]>();
      }

      const stepMap = new Map<string, IPracticeSetupStep[]>();

      for (const step of steps) {
        const definition = this._findStepDefinition(step.id);
        if (definition) {
          const route = definition.getRoute(brand, practice);
          const existing = stepMap.get(route) ?? [];
          existing.push(step);
          stepMap.set(route, existing);
        }
      }

      return stepMap;
    });

    this.markCompleteTooltip = computed(() => {
      const step = this.matchingStep();

      if (this.stepStore.mutating()) {
        return { title: 'Completing step...', errors: [] as string[] };
      }

      if (!step) {
        return { title: '', errors: [] as string[] };
      }

      const liveErrors = this.stepStore.liveValidationErrors();
      if (liveErrors?.length) {
        return {
          title: 'Cannot complete step',
          errors: liveErrors,
        };
      }

      if (step.validationErrors && step.validationErrors.length > 0) {
        return {
          title: 'Cannot complete step',
          errors: step.validationErrors,
        };
      }

      if (!this.stepStore.canComplete()) {
        return {
          title: 'Complete prerequisites first',
          errors: [] as string[],
        };
      }

      return { title: 'Mark this step as complete', errors: [] as string[] };
    });
  }

  isStepComplete(step: IPracticeSetupStep): boolean {
    return step.status === PracticeSetupStepStatus.Complete;
  }

  selectStep(index: number): void {
    const steps = this.matchingSteps();
    const step = steps[index];
    if (!step || this.isStepComplete(step)) {
      return;
    }
    if (index >= 0 && index < steps.length) {
      this.selectedStepIndex.set(index);
    }
  }

  async navigateToWizard(): Promise<void> {
    const brand = this.brand();
    const practice = this.practice();
    if (!brand || !practice) {
      return;
    }
    const step = this.matchingStep();
    const queryParams = step?.category ? { category: step.category } : {};
    await this._router.navigate([`${brand.slug}/${practice.slug}/setup`], {
      queryParams,
    });
  }

  async markComplete(overrideCompleted: boolean = false): Promise<void> {
    const step = this.matchingStep();

    if (
      !step ||
      (!this.isManagement() &&
        (!this.stepStore.canComplete() || this.stepStore.mutating()))
    ) {
      return;
    }

    const staffer = await snapshotDefined(this._organisationService.staffer$);
    if (!staffer) {
      return;
    }

    this.store.completeStep({
      stepId: step.id,
      completedBy: staffer,
      overrideCompleted,
    });

    this._snackBar.open('Step marked as complete');
  }

  private _computeMatchingSteps(): IPracticeSetupStep[] {
    const url = this._currentUrl();
    const normalizedUrl = this._normalizeUrl(url || '');
    const routeMap = this._routeToStepMap();

    let longestPrefix = '';
    let matchedSteps: IPracticeSetupStep[] = [];

    for (const [routePrefix, steps] of routeMap) {
      if (
        normalizedUrl === routePrefix ||
        (normalizedUrl.startsWith(routePrefix) &&
          normalizedUrl[routePrefix.length] === '/')
      ) {
        if (routePrefix.length > longestPrefix.length) {
          longestPrefix = routePrefix;
          matchedSteps = steps;
        }
      }
    }

    return matchedSteps.slice().sort((stepA, stepB) => {
      const regionSteps = this.store.regionFilteredSteps();
      const aIndex = regionSteps.findIndex(({ id }) => id === stepA.id);
      const bIndex = regionSteps.findIndex(({ id }) => id === stepB.id);
      return aIndex - bIndex;
    });
  }

  private _findStepDefinition(
    stepId: string
  ): IPracticeSetupStepDefinition | undefined {
    return this.store.regionFilteredSteps().find((step) => step.id === stepId);
  }

  private _normalizeUrl(url: string): string {
    const withoutParams = url.split('?')[0].split('#')[0];
    return withoutParams.startsWith('/')
      ? withoutParams.substring(1)
      : withoutParams;
  }
}
